import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SpaBadge } from '@/components/ui/spa-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TypeBadge } from '@/components/ui/type-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { Search, MapPin, Filter, X } from 'lucide-react';
import { format } from 'date-fns';

interface Opportunity {
  id: string;
  title: string;
  description: string;
  type: 'job' | 'tender' | 'jv' | 'training' | 'service_offer';
  sector: 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';
  country: string;
  city: string | null;
  province: string | null;
  remote: boolean;
  eligibility: 'local' | 'diaspora_welcome' | 'international';
  spa_linked: boolean;
  spa_pillar: 'critical_minerals' | 'industrialization' | 'infrastructure' | 'governance' | null;
  deadline: string | null;
  created_at: string;
  company: {
    name: string;
    logo_url: string | null;
  };
}

const DRC_PROVINCES = [
  'Kinshasa', 'Kongo Central', 'Kwango', 'Kwilu', 'Mai-Ndombe',
  'Kasai', 'Kasai-Central', 'Kasai-Oriental', 'Lomami', 'Sankuru',
  'Maniema', 'Sud-Kivu', 'Nord-Kivu', 'Ituri', 'Haut-Uele',
  'Tshopo', 'Bas-Uele', 'Nord-Ubangi', 'Mongala', 'Sud-Ubangi',
  'Equateur', 'Tshuapa', 'Tanganyika', 'Haut-Lomami', 'Lualaba', 'Haut-Katanga',
] as const;

const TYPES = ['job', 'tender', 'jv', 'training', 'service_offer'] as const;
const SECTORS = ['mining', 'processing', 'infrastructure', 'industrial', 'services', 'other'] as const;
const ELIGIBILITY = ['local', 'diaspora_welcome', 'international'] as const;

export default function OpportunitiesDirectory() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [sectorFilters, setSectorFilters] = useState<string[]>([]);
  const [eligibilityFilters, setEligibilityFilters] = useState<string[]>([]);
  const [spaOnly, setSpaOnly] = useState(false);
  const [provinceFilter, setProvinceFilter] = useState<string>('all');

  // Read URL params on mount
  useEffect(() => {
    const urlProvince = searchParams.get('province');
    const urlSpa = searchParams.get('spa');
    if (urlProvince) {
      setProvinceFilter(urlProvince);
      setShowFilters(true);
    }
    if (urlSpa === 'true') {
      setSpaOnly(true);
      setShowFilters(true);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const { data, error } = await supabase
        .from('opportunities')
        .select('*, company:companies(name, logo_url)')
        .eq('status', 'open')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOpportunities((data as unknown as Opportunity[]) || []);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFilter = (
    item: string,
    array: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  const clearFilters = () => {
    setTypeFilters([]);
    setSectorFilters([]);
    setEligibilityFilters([]);
    setSpaOnly(false);
    setProvinceFilter('all');
    setSearchQuery('');
  };

  const hasActiveFilters = typeFilters.length > 0 || sectorFilters.length > 0 || eligibilityFilters.length > 0 || spaOnly || provinceFilter !== 'all' || searchQuery;

  // Show province filter when DR Congo opps exist or URL param is set
  const showProvinceFilter = provinceFilter !== 'all' || opportunities.some(o => o.country === 'DR Congo');

  const filteredOpportunities = opportunities.filter(opp => {
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        opp.title.toLowerCase().includes(query) ||
        opp.description.toLowerCase().includes(query) ||
        opp.company.name.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Type filter
    if (typeFilters.length > 0 && !typeFilters.includes(opp.type)) return false;

    // Sector filter
    if (sectorFilters.length > 0 && !sectorFilters.includes(opp.sector)) return false;

    // Eligibility filter
    if (eligibilityFilters.length > 0 && !eligibilityFilters.includes(opp.eligibility)) return false;

    // Province filter
    if (provinceFilter !== 'all' && opp.province !== provinceFilter) return false;

    // SPA filter
    if (spaOnly && !opp.spa_linked) return false;

    return true;
  });

  return (
    <Layout>
      <div className="container py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">{t('opportunities.directory.title')}</h1>
          <p className="mt-2 text-muted-foreground">{t('opportunities.directory.description')}</p>
        </div>

        {/* Quick-filter chips */}
        <div className="mb-4 flex flex-wrap gap-2">
          {[
            { key: 'all', label: t('opportunities.directory.all'), filter: [] },
            { key: 'job', label: t('opportunities.types.job'), filter: ['job'] },
            { key: 'tender', label: t('opportunities.types.tender'), filter: ['tender'] },
            { key: 'jv', label: t('opportunities.types.jv'), filter: ['jv'] },
            { key: 'training', label: t('opportunities.types.training'), filter: ['training'] },
            { key: 'service_offer', label: t('opportunities.directory.serviceOffers'), filter: ['service_offer'] },
          ].map(chip => {
            const isActive = chip.key === 'all'
              ? typeFilters.length === 0
              : typeFilters.length === 1 && typeFilters[0] === chip.key;
            return (
              <Button
                key={chip.key}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTypeFilters(chip.filter)}
              >
                {chip.label}
              </Button>
            );
          })}
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('opportunities.directory.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <Filter className="h-4 w-4" />
            {t('common.filter')}
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-1">
                {typeFilters.length + sectorFilters.length + eligibilityFilters.length + (spaOnly ? 1 : 0)}
              </Badge>
            )}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters} className="gap-2">
              <X className="h-4 w-4" />
              {t('common.reset')}
            </Button>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6">
            <CardContent className="grid gap-6 p-6 sm:grid-cols-2 lg:grid-cols-4">
              {/* Type */}
              <div className="space-y-3">
                <Label className="font-semibold">{t('opportunities.filters.type')}</Label>
                <div className="space-y-2">
                  {TYPES.map(type => (
                    <Label key={type} className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={typeFilters.includes(type)}
                        onCheckedChange={() => toggleFilter(type, typeFilters, setTypeFilters)}
                      />
                      <span className="text-sm">{t(`opportunities.types.${type}`)}</span>
                    </Label>
                  ))}
                </div>
              </div>

              {/* Sector */}
              <div className="space-y-3">
                <Label className="font-semibold">{t('opportunities.filters.sector')}</Label>
                <div className="space-y-2">
                  {SECTORS.map(sector => (
                    <Label key={sector} className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={sectorFilters.includes(sector)}
                        onCheckedChange={() => toggleFilter(sector, sectorFilters, setSectorFilters)}
                      />
                      <span className="text-sm">{t(`sectors.${sector}`)}</span>
                    </Label>
                  ))}
                </div>
              </div>

              {/* Eligibility */}
              <div className="space-y-3">
                <Label className="font-semibold">{t('opportunities.filters.eligibility')}</Label>
                <div className="space-y-2">
                  {ELIGIBILITY.map(elig => (
                    <Label key={elig} className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={eligibilityFilters.includes(elig)}
                        onCheckedChange={() => toggleFilter(elig, eligibilityFilters, setEligibilityFilters)}
                      />
                      <span className="text-sm">{t(`eligibility.${elig}`)}</span>
                    </Label>
                  ))}
                </div>
              </div>

              {/* SPA Only */}
              <div className="space-y-3">
                <Label className="font-semibold">SPA</Label>
                <Label className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={spaOnly}
                    onCheckedChange={(checked) => setSpaOnly(checked as boolean)}
                  />
                  <span className="text-sm">{t('opportunities.filters.spaOnly')}</span>
                </Label>

                {/* Province filter */}
                {showProvinceFilter && (
                  <div className="mt-3 space-y-2">
                    <Label className="font-semibold">{t('opportunities.filters.province')}</Label>
                    <Select value={provinceFilter} onValueChange={setProvinceFilter}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('opportunities.filters.province')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('misc.allProvinces')}</SelectItem>
                        {DRC_PROVINCES.map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-16 rounded-full" />
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </div>
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-full" />
                </CardContent>
                <CardFooter className="border-t p-4">
                  <Skeleton className="h-9 w-full" />
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : filteredOpportunities.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">{t('opportunities.directory.noResults')}</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredOpportunities.map(opp => (
              <Card
                key={opp.id}
                className={`transition-shadow hover:shadow-lg ${
                  opp.spa_linked ? 'spa-card-highlight' : ''
                }`}
              >
                <CardContent className="p-6">
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <TypeBadge type={opp.type} />
                    {opp.spa_linked && <SpaBadge pillar={opp.spa_pillar || undefined} size="sm" />}
                  </div>

                  <h3 className="mb-2 text-lg font-semibold line-clamp-2">{opp.title}</h3>

                  <p className="mb-3 text-sm text-muted-foreground">{opp.company.name}</p>

                  <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {opp.city ? `${opp.city}, ` : ''}{opp.country}
                    </span>
                    {opp.remote && (
                      <Badge variant="outline" className="text-xs">
                        {t('opportunities.detail.remote')}
                      </Badge>
                    )}
                  </div>

                  <SectorBadge sector={opp.sector} />

                  <p className="mt-4 text-sm text-muted-foreground line-clamp-2">
                    {opp.description}
                  </p>

                  {opp.deadline && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      {t('opportunities.detail.deadline')}: {format(new Date(opp.deadline), 'MMM d, yyyy')}
                    </p>
                  )}
                </CardContent>
                <CardFooter className="border-t p-4">
                  <Button asChild variant="outline" className="w-full">
                    <Link to={`/opportunities/${opp.id}`}>
                      {t('opportunities.directory.viewDetails')}
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}